import { css } from '@/styled-system/css';
import { Anchor, Box, Paragraph } from '@zillow/constellation-10';
import { useEffect, useRef, useState } from 'react';

type ReadMoreTextProps = {
  text: string;
  /**
   * Maximum number of lines to show before truncating.
   * @default 4
   */
  maxLines?: number;
  /**
   * Custom "Read more" text
   * @default "Read more"
   */
  readMoreText?: string;
  /**
   * Custom "Read less" text
   * @default "Read less"
   */
  readLessText?: string;
};

const DEFAULT_MAX_LINES = 4;

export default function ReadMoreText({
  text,
  maxLines = DEFAULT_MAX_LINES,
  readMoreText = 'Read more',
  readLessText = 'Read less',
}: ReadMoreTextProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    // Check if text is actually truncated
    const element = textRef.current;
    if (element) {
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [text, maxLines]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Line-based truncation styles using Panda CSS
  const truncationStyles = css.raw({
    lineClamp: maxLines,
  });

  return (
    <Box>
      <Paragraph
        ref={textRef}
        css={{
          ...(!isExpanded && truncationStyles),
        }}
      >
        {text}
      </Paragraph>

      {isTruncated && (
        <Paragraph textStyle="body-xs-bold" css={{ marginTop: 'tighter' }}>
          <Anchor asChild href="#" onClick={handleToggle}>
            <button type="button">{isExpanded ? readLessText : readMoreText}</button>
          </Anchor>
        </Paragraph>
      )}
    </Box>
  );
}
